import type { AuthData } from '../auth/index.js'
import { OAuth } from '../auth/index.js'
import { userSlice } from '../features/user/user-slice.js'
import { loginProcessMutex } from './auth-mutex.js'

interface AuthStateSlice {
  user: { authData?: AuthData }
}

/**
 * Token access for raw-fetch streaming endpoints — the reauth parity twin of
 * baseQueryWithReauth. Waits out any in-flight login, refreshes proactively
 * when the session is expired (or when forced after a 401), and shares the
 * login mutex so refreshes never race.
 */
export const getFreshIdToken = async (
  api: { getState: () => unknown; dispatch: (action: unknown) => unknown },
  opts: { forceRefresh?: boolean } = {}
): Promise<string | null> => {
  await loginProcessMutex.waitForUnlock()
  const state = api.getState() as AuthStateSlice
  const authData = state.user.authData
  if (!authData) {
    return null
  }
  if (!opts.forceRefresh && !OAuth.isSessionExpired(authData)) {
    return authData.idToken.jwtToken
  }
  if (loginProcessMutex.isLocked()) {
    // Another caller is already refreshing — wait and read the result.
    await loginProcessMutex.waitForUnlock()
    const refreshed = (api.getState() as AuthStateSlice).user.authData
    return refreshed?.idToken.jwtToken ?? null
  }
  const release = await loginProcessMutex.acquire()
  try {
    const sess = await OAuth.getSession()
    if (sess?.idToken.payload.sub) {
      api.dispatch(userSlice.actions.setAuth(sess))
      return sess.idToken.jwtToken
    }
    OAuth.logout()
    api.dispatch(userSlice.actions.signout())
    return null
  } finally {
    release()
  }
}
